import { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import styles from './styles.module.scss';

const IMPACT_CONFIG = {
  high: { color: '#EF2A35', label: 'High' },
  medium: { color: '#F0B100', label: 'Med' },
  med: { color: '#F0B100', label: 'Med' },
  low: { color: '#22C55E', label: 'Low' },
};

const normalizeImpact = impact => (impact ?? '').toLowerCase().trim();

const OverviewReport = ({ overview }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const wrapperRef = useRef(null);

  const {
    review_snippet: summary,
    areas_of_improvement: areasOfImprovement,
    whats_working: whatsWorking,
    primary_audience: primaryAudience,
    primary_metrics: primaryMetrics,
  } = overview;

  const hasImprovements = Array.isArray(areasOfImprovement) && areasOfImprovement.length > 0;
  const hasWhatsWorking = Array.isArray(whatsWorking) && whatsWorking.length > 0;
  const hasMetrics = Array.isArray(primaryMetrics) && primaryMetrics.length > 0;

  return (
    <div className={styles['overview-report']}>
      <h2 className={styles['overview-heading']}>
        Overview
      </h2>

      {summary && (
        <p className={styles['overview-summary']}>
          {summary}
        </p>
      )}

      <div
        ref={wrapperRef}
        className={styles['collapsible-wrapper']}
        style={isExpanded && wrapperRef.current ? { maxHeight: wrapperRef.current.scrollHeight } : undefined}
      >
        <div className={styles['sections-container']}>
          {hasImprovements && (
            <div>
              <div className={styles['table-wrapper']}>
                <table className={styles['improvement-table']}>
                  <thead>
                    <tr>
                      <th className={`${styles['table-header-cell']} ${styles['col-impact']}`}>
                        Impact
                      </th>
                      <th className={`${styles['table-header-cell']} ${styles['col-issue']}`}>
                        Issue
                      </th>
                      <th className={`${styles['table-header-cell']} ${styles['col-improve']}`}>
                        What to improve
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {areasOfImprovement.map((row, index) => {
                      const impactConfig = IMPACT_CONFIG[normalizeImpact(row.impact)];

                      return (
                        <tr key={index}>
                          <td className={`${styles['table-cell']} ${styles['col-impact']}`}>
                            <span className={styles['impact-badge']}>
                              <span
                                className={styles['impact-dot']}
                                style={{ backgroundColor: impactConfig?.color }}
                              />
                              <span className={styles['impact-text']}>
                                {impactConfig?.label ?? row.impact}
                              </span>
                            </span>
                          </td>
                          <td className={styles['table-cell']}>
                            {row.issue}
                          </td>
                          <td className={`${styles['table-cell']} ${styles['table-cell-improve']}`}>
                            {row.what_to_improve}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {hasWhatsWorking && (
            <div>
              <h3 className={styles['section-heading']}>
                What's working
              </h3>
              <ul className={styles['bullet-list']}>
                {whatsWorking.map((item, index) => (
                  <li key={index} className={styles['bullet-item']}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {primaryAudience && (
            <div>
              <p className={styles['section-heading']}>
                Primary audience
              </p>
              <p className={styles['section-body']}>
                {primaryAudience}
              </p>
            </div>
          )}
          {hasMetrics && (
            <div>
              <h3 className={styles['section-heading']}>
                Primary metrics
              </h3>
              <p className={styles['section-body']}>
                {primaryMetrics.map((metric, index) => (
                  <span key={index}>
                    {index > 0 && (
                      <span className={styles['metrics-separator']}>
                        |
                      </span>
                    )}
                    <span className={styles['metric-text']}>
                      {metric}
                    </span>
                  </span>
                ))}
              </p>
            </div>
          )}

        </div>

        {!isExpanded && <div className={styles['blur-overlay']} />}
      </div>

      <div className={styles['toggle-btn-wrapper']}>
        <button
          className={styles['toggle-btn']}
          onClick={() => setIsExpanded(prev => !prev)}
        >
          {isExpanded ? 'See less' : 'See more'}
        </button>
      </div>
    </div>
  );
};

OverviewReport.propTypes = {
  overview: PropTypes.shape({
    review_snippet: PropTypes.string,
    areas_of_improvement: PropTypes.arrayOf(PropTypes.shape({
      impact: PropTypes.string,
      issue: PropTypes.string,
      what_to_improve: PropTypes.string,
    })),
    whats_working: PropTypes.arrayOf(PropTypes.string),
    primary_audience: PropTypes.string,
    primary_metrics: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default OverviewReport;
